import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import {
  fetchLeadGroupData,
  fetchAllGroupByLeadId,
  UpdateGroupsForLead,
} from "../Services/APIService";
import Select from "react-select";
import { ActionMeta, MultiValue } from "react-select";
import { group } from "console";
import selectStyles from "../js/selectStyles.js";

interface LeadsGroup {
  groupId: number;
  groupName: string;
}

interface SelectOption {
  value: number;
  label: string;
  LeadsData: LeadsGroup;
}

interface ValidationErrors {
  listOfLeadGroups?: string;
}

interface LeadDetailsViewProps {
  leadId: number;
}

const requiredField = <span className="text-danger ms-1">*</span>;

const LeadGroupAssign: React.FC<LeadDetailsViewProps> = ({ leadId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [leadGroupsOptions, setLeadGroupsOptions] = useState<SelectOption[]>(
    []
  );
  const [selectedLeadGroups, setSelectedLeadGroups] = useState<SelectOption[]>(
    []
  );
  const [initialSelectedLeadGroups, setInitialSelectedLeadGroups] = useState<
    SelectOption[]
  >([]);
  const [isLoadingLeads, setIsLoadingLeads] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isCardVisible, setIsCardVisible] = useState(false); // Start with card body hidden

  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingLeads(true);
      setLeadGroupsOptions([]);
      setSelectedLeadGroups([]);
      setInitialSelectedLeadGroups([]);

      try {
        const [allleadgroups, allassigngroupbyleadid] = await Promise.all([
          fetchLeadGroupData(),
          fetchAllGroupByLeadId(leadId ?? 0),
        ]);

        const activeGroups = allleadgroups.filter(
          (group) => group.isActive === true
        );

        const options = activeGroups.map((leads) => ({
          value: leads.groupId,
          label: leads.groupName,
          LeadsData: leads,
        }));

        setLeadGroupsOptions(options);

        if (allassigngroupbyleadid.groups.length > 0) {
          const selected = options.filter((option) =>
            allassigngroupbyleadid.groups.some(
              (groupLead) => groupLead.groupId === option.value
            )
          );

          setSelectedLeadGroups(selected);
          setInitialSelectedLeadGroups(selected);
        } else {
          setSelectedLeadGroups([]);
          setInitialSelectedLeadGroups([]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load required data");
      } finally {
        setIsLoadingLeads(false);
      }
    };

    if (leadId) {
      fetchData();
    }
  }, [leadId]);

  const handleMultiSelectChange = (
    newValue: MultiValue<SelectOption>,
    actionMeta: ActionMeta<SelectOption>
  ) => {
    if (!newValue || newValue.length === 0) {
      setSelectedLeadGroups([]);
      return;
    }

    const selectedOptions = Array.isArray(newValue) ? newValue : [newValue];
    setSelectedLeadGroups(selectedOptions);

    if (errors.listOfLeadGroups) {
      setErrors({
        ...errors,
        listOfLeadGroups: undefined,
      });
    }
  };

  const validateAssignmentData = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!selectedLeadGroups || selectedLeadGroups.length === 0) {
      newErrors.listOfLeadGroups = "Please select at least one group";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleAssignmentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateAssignmentData()) {
      return;
    }

    if (!leadId) {
      toast.error("leadid not found");
      return;
    }

    try {
      setIsLoading(true);

      const tenantId = localStorage.getItem("tenantId");
      const userId = localStorage.getItem("UserId");

      if (!tenantId || !userId) {
        throw new Error("Required session data not found");
      }

      // Get current and initial selected lead IDs
      const currentSelectedLeadgroup = selectedLeadGroups;
      const initialSelectedLeadsgroupList = initialSelectedLeadGroups;

      // Find leads to remove (in initial but not in current selection)
      const leadgroupToRemove = initialSelectedLeadsgroupList.filter(
        (initialLead) =>
          !currentSelectedLeadgroup.some(
            (currentLead) => currentLead.value === initialLead.value
          )
      );

      const leadgroupToAdd = currentSelectedLeadgroup;

      // Process removals
      if (leadgroupToRemove.length > 0) {
        const deleteData = {
          leadId: leadId,
          groups: leadgroupToRemove.map((lead) => ({
            groupId: lead.value,
          })),
          isAdd: false,
          tenantId: parseInt(tenantId),
          createdBy: parseInt(userId),
        };

        const removeResult = await UpdateGroupsForLead(deleteData);

        if (!removeResult.isUpdated) {
          throw new Error(removeResult.message || "Failed to unassign leads");
        }
      }

      // Process additions
      if (leadgroupToAdd.length > 0) {
        const createData = {
          leadId: leadId,
          groups: leadgroupToAdd.map((lead) => ({
            groupId: lead.value,
          })),
          isAdd: true,
          tenantId: parseInt(tenantId),
          createdBy: parseInt(userId),
        };

        const addResult = await UpdateGroupsForLead(createData);

        if (!addResult.isUpdated) {
          throw new Error(addResult.message || "Failed to assign new leads");
        }
      }

      if (leadgroupToAdd.length > 0 || leadgroupToRemove.length > 0) {
        toast.success("Leads Group assigned successfully");
        setInitialSelectedLeadGroups([...currentSelectedLeadgroup]);
      } else {
        toast.error("No changes in lead assignments");
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleCardVisibility = () => {
    setIsCardVisible(!isCardVisible);
  };

  return (
    <div className="card">
      <div className="card-header carHeaderWithBtn d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0">Lead Present Group</h5>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={toggleCardVisibility}
          aria-expanded={isCardVisible}
          aria-controls="card-body-content"
        >
          <i className={`bi ${isCardVisible ? "bi-dash" : "bi-plus"}`}></i>
        </button>
      </div>
      {isCardVisible && (
        <div id="card-body-content" className="card-body">
          <form>
            <div className="row">
              <div className="col-12 col-lg-12 mb-3">
                <label className="form-label">
                  Assign Groups{requiredField}
                </label>
                <Select
                  options={leadGroupsOptions}
                  value={selectedLeadGroups}
                  onChange={handleMultiSelectChange}
                  isMulti
                  className={`basic-multi-select ${
                    errors.listOfLeadGroups ? "is-invalid" : ""
                  }`}
                  classNamePrefix="select"
                  placeholder="Select leads to assign to group"
                  isLoading={isLoadingLeads}
                  noOptionsMessage={() => "No leads available"}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectStyles}
                />
                {errors.listOfLeadGroups && (
                  <div className="text-danger mt-1 small">
                    {errors.listOfLeadGroups}
                  </div>
                )}
                <div className="form-text">
                  Select multiple groups to assign to this leads
                </div>
              </div>
            </div>
          </form>
          <button
            type="button"
            className="btn btn-primary me-2"
            onClick={handleAssignmentSubmit}
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Assign Group"}
          </button>
        </div>
      )}
    </div>
  );
};

export default LeadGroupAssign;
